import Model, { attr } from '@ember-data/model';
import {
  type ContactCustomPropertyInfo,
  type ContactCustomPropertyInfoSettings,
  type ContactCustomPropertyType,
} from 'donor-elf-web/services/contacts-cache';

export default class BulkActionModel extends Model {
  @attr('string') actionType!: BulkActionType;
  @attr('string') status!: BulkActionStatus;
  @attr() custom?: BulkActionCustomType;
  @attr() response?: BulkActionResponseType;
}

export interface BulkActionCustomSetPledgeCampaign {
  pledge_ids: string[];
  campaign_id: string;
}

export type BulkActionType =
  | 'add_task'
  | 'set_custom_property'
  | 'remove_custom_property'
  | 'delete_custom_property'
  | 'add_to_journal'
  | 'delete_contacts'
  | 'rename_custom_property'
  | 'custom_property_list_option_delete'
  | 'custom_property_list_option_rename'
  | 'custom_property_list_option_reorder'
  | 'set_line_item_campaign'
  | 'set_pledge_campaign'
  | 'change_parent_campaign'
  | 'merge_contacts'
  | 'delete_campaign';

export type BulkActionStatus =
  | 'not_started'
  | 'processing'
  | 'completed'
  | 'error';

export type BulkActionCustomType =
  | BulkActionCustomAddTask
  | BulkActionCustomSetCustomProperty
  | BulkActionCustomRemoveCustomProperty
  | BulkActionCustomDeleteCustomProperty
  | BulkActionCustomAddToJournal
  | BulkActionCustomDeleteContacts
  | BulkActionCustomRenameCustomProperty
  | BulkActionCustomListCustomPropertyOptionDelete
  | BulkActionCustomListCustomPropertyOptionRename
  | BulkActionCustomListCustomPropertyOptionReorder
  | BulkActionCustomSetLineItemCampaign
  | BulkActionCustomSetPledgeCampaign
  | BulkActionCustomChangeParentCampaign
  | BulkActionCustomMergeContacts
  | BulkActionCustomDeleteCampaign;

export type BulkActionResponseType =
  | BulkActionResponseContactIds
  | BulkActionResponseAddTask
  | BulkActionResponseDuplicateJournal
  | BulkActionResponseIncludesCustomPropertyInfo
  | BulkActionResponseDeleteContacts
  | BulkActionResponseLineItemIds
  | BulkActionResponsePledgeIds
  | BulkActionResponseCampaignId;

export interface BulkActionCustomAddTask {
  contact_ids: string[];
  action_id: string;
  medium_id?: string;
  due_date?: string;
  completed_date?: string;
  result_id?: string;
  summary?: string;
  notes?: string;
  journal_stage_id?: string;
  assigned_user_ids?: string[];
}

export interface BulkActionCustomSetCustomProperty {
  contact_ids: string[];
  new_property: boolean;
  l: string;
  v: any;
  t: ContactCustomPropertyType;
  o?: string[];
  s?: ContactCustomPropertyInfoSettings;
  list_options_action?: 'add' | 'remove';
}

export interface BulkActionCustomRemoveCustomProperty {
  contact_ids: string[];
  property_label: string;
}

export interface BulkActionCustomDeleteCustomProperty {
  property_label: string;
}

export interface BulkActionCustomAddToJournal {
  contact_ids: string[];
  journal_id: string;
}

export interface BulkActionCustomDeleteContacts {
  contact_ids: string[];
}

export interface BulkActionCustomRenameCustomProperty {
  property_label: string;
  new_label: string;
}

export interface BulkActionCustomListCustomPropertyOptionDelete {
  property_label: string;
  option: string;
}

export interface BulkActionCustomListCustomPropertyOptionRename {
  property_label: string;
  old_option: string;
  new_option: string;
}

export interface BulkActionCustomListCustomPropertyOptionReorder {
  property_label: string;
  options: string[];
}

export interface BulkActionCustomSetLineItemCampaign {
  campaign_id: string;
  line_item_ids: string[];
}

export interface BulkActionCustomChangeParentCampaign {
  campaign_id: string;
  new_parent_campaign_id?: string;
}

export interface BulkActionCustomSetPledgeCampaign {
  campaign_id: string;
  pledge_ids: string[];
}

export interface BulkActionCustomMergeContacts {
  contact_id_to_keep: string;
  contact_id_to_merge: string;
  attributes: any
}

export interface BulkActionCustomDeleteCampaign {
  campaign_id: string;
  move_to_campaign_id?: string;
}

export interface BulkActionResponseContactIds {
  contact_ids: string[];
}

export interface BulkActionResponseAddTask {
  task_group_id: string;
}

export interface BulkActionResponseDuplicateJournal {
  new_journal_id: string;
}

export interface BulkActionResponseIncludesCustomPropertyInfo {
  contact_ids: string[];
  custom_property_info: ContactCustomPropertyInfo;
}

export interface BulkActionResponseDeleteContacts {
  contact_ids_deleted: string[];
  contact_ids_hidden: string[];
}

export interface BulkActionResponseIncludesUpdatedFundProperties {
  contact_ids: string[];
  updated_fund_properties: ContactCustomPropertyInfo[];
}

export interface BulkActionResponseLineItemIds {
  line_item_ids: string[];
}

export interface BulkActionResponseCampaignId {
  campaign_id: string;
}
export interface BulkActionResponsePledgeIds {
  pledge_ids: string[];
}

declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    'bulk-action': BulkActionModel;
  }
}
